// ── Editorial modal primitives ────────────────────────────────────────────
// Match the platform's editorial modal recipe: a tinted icon chip + mono
// eyebrow + serif title in the header, a label-above-control Field, and a
// footer with a mono meta line. Purely presentational — no logic lives here.
//
// #629 M0: these lived at the bottom of `pages/admin/UserManagementPage.tsx`
// (renamed to `UserRoster.tsx` in M1),
// private to that file. That privacy is what let the create-user dialog in
// `AdminUsersPage` hand-roll a raw `DialogHeader` while the seven dialogs in
// the body all used this recipe — same screen, two modal vocabularies. Moving
// them here is a pure move with zero render change; it is what makes the M1
// consolidation real rather than nominal.

import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
// Direct module paths, not the `@/components/editorial` barrel: the barrel
// re-exports this file (#3169), and importing it back would be a cycle.
import { FieldLabel } from '@/components/editorial/FieldLabel';
import { MonoLabel } from '@/components/editorial/MonoLabel';
import { DialogDescription, DialogTitle } from '@/components/ui/dialog';
import type { LucideIcon } from 'lucide-react';

export type ModalTone = 'indigo' | 'amber' | 'rose';

// The tone table is a frozen constant, not state — exporting it beside the
// components costs nothing at runtime and keeps the recipe in one file, which
// is the whole point of the M0 extraction. HMR's only-components rule does not
// know about `allowConstantExport`, so waive it here rather than split a
// three-line map into its own module.
// eslint-disable-next-line react-refresh/only-export-components
export const CHIP_TONE: Record<ModalTone, string> = {
  indigo: 'bg-stevens-indigo/10 text-stevens-indigo',
  amber: 'bg-status-warn text-status-warn-fg',
  rose: 'bg-status-danger text-status-danger-fg',
};

/** Editorial dialog header: tinted icon chip + mono eyebrow + serif title. */
export function ModalHeader({
  icon: Icon,
  eyebrow,
  title,
  description,
  tone = 'indigo',
}: {
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  description?: React.ReactNode;
  tone?: ModalTone;
}) {
  return (
    <header className="flex items-start gap-3 px-6 pt-6 pb-4">
      <span className={cn('inline-flex h-10 w-10 items-center justify-center rounded-full flex-shrink-0', CHIP_TONE[tone])}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0 pr-6">
        <MonoLabel className="block mb-1">{eyebrow}</MonoLabel>
        {/* #1474: the dialog heading sits on `card-title` (16px Fraunces 400),
            the rung design-grammar §2 gives "card / dialog heading". The
            platform's older idiom was `font-serif text-2xl font-medium` — 24px,
            a rung the ladder does not have — hand-rolled in ~20 files. The
            ladder wins over the idiom; this is the one place that spells it. */}
        <DialogTitle className="text-card-title font-serif font-normal leading-[1.25]">
          {title}
        </DialogTitle>
        {description !== undefined && (
          <DialogDescription className="mt-1.5 text-body text-stevens-ink-3">{description}</DialogDescription>
        )}
      </div>
    </header>
  );
}

/**
 * Editorial dialog footer. A mono meta line is optional — pass it only when
 * it carries live, non-redundant info (e.g. a running selection count); for
 * simple confirms the buttons just right-align on their own.
 *
 * `lead` is the slot for a left side that is more than one label — a label
 * with a `PendingDecision` marker beside it, or a label whose type differs
 * from the `meta` recipe. Either one pushes the actions right. `wrap` is for
 * the footers whose left side can outgrow the line (the three medical modals). #3169: nine
 * dialogs hand-wrote this footer's class string because the primitive had no
 * slot for their left side. `className` is for a footer that differs from the
 * recipe by one class (`flex-wrap`), so the string never has to be copied.
 */
export function ModalFooter({
  meta,
  lead,
  wrap,
  className,
  children,
}: {
  meta?: React.ReactNode;
  lead?: React.ReactNode;
  /** The footer wraps when the lead and the buttons do not fit one line; the
   *  button group then keeps to the end edge (`ms-auto`) and may shrink. */
  wrap?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const hasLeft = meta != null || lead != null;
  return (
    <footer className={cn('flex items-center gap-3 border-t px-6 py-3.5 flex-shrink-0', wrap && 'flex-wrap', hasLeft ? 'justify-between' : 'justify-end', className)}>
      {lead ?? (meta != null && (
        <MonoLabel className="min-w-0 truncate text-[11px]">{meta}</MonoLabel>
      ))}
      <div className={cn('flex items-center gap-2', wrap ? 'ms-auto' : 'flex-shrink-0')}>{children}</div>
    </footer>
  );
}

/**
 * The dialog text input (#3169). Its class string was copied 38 times across
 * eleven dialogs, so a focus colour or radius change had to be made eleven
 * times. `forwardRef` so react-hook-form's `register` and a caller's own ref
 * keep working; `className` merges through `cn`, so a caller adds `font-mono`
 * or swaps the type rung without restating the recipe.
 */
export const MODAL_INPUT_CLASS =
  'w-full rounded-lg border border-input bg-background px-3 py-2 text-body outline-none focus:border-stevens-indigo transition-colors';

export const ModalInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function ModalInput({ className, ...rest }, ref) {
    return <input ref={ref} className={cn(MODAL_INPUT_CLASS, className)} {...rest} />;
  },
);

/** The dialog textarea: `ModalInput`'s recipe plus `resize-none`, which every
 *  dialog textarea but one carried. A caller passes `resize-y` to opt back in. */
export const ModalTextarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function ModalTextarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={cn(MODAL_INPUT_CLASS, 'resize-none', className)} {...rest} />;
  },
);

/** Label-above-control field with a mono uppercase label. */
export function Field({ label, htmlFor, className, children }: { label: React.ReactNode; htmlFor?: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <FieldLabel htmlFor={htmlFor} className="mb-1.5">{label}</FieldLabel>
      {children}
    </div>
  );
}
